import type { CompletionRow, StreakRow } from '../progress/hooks.js';

/**
 * Snapshot of progress state used to compute achievement unlocks.
 *
 * Pure data in → pure numbers out for each definition. No hooks, no I/O,
 * keeps the unlock rules trivially testable. Course-tree-derived counts
 * (skills/units) and time-of-day stats are computed by the caller /
 * `deriveTimeStats` so definitions stay synchronous and pure.
 */
export interface ProgressSnapshot {
  completions: Record<string, CompletionRow>;
  streak: StreakRow | undefined;
  totalXp: number;
  vocabCount: number;
  hasProAccess: boolean;
  /** Skills where every lesson is completed (from the course tree). */
  skillsCompleted: number;
  /** Units where every lesson is completed (from the course tree). */
  unitsCompleted: number;
  time: CompletionTimeStats;
}

/** Time-of-day facts mined from completion timestamps (user-local time). */
export interface CompletionTimeStats {
  /** ≥1 lesson finished before 08:00. */
  hasEarlyBird: boolean;
  /** ≥1 lesson finished at/after 22:00. */
  hasNightOwl: boolean;
  /** ≥1 lesson on a Saturday AND ≥1 on a Sunday. */
  hasWeekendPair: boolean;
  /** Most lessons finished within a single calendar day. */
  maxLessonsInOneDay: number;
}

export function deriveTimeStats(
  completions: Record<string, CompletionRow>,
): CompletionTimeStats {
  let hasEarlyBird = false;
  let hasNightOwl = false;
  let sawSaturday = false;
  let sawSunday = false;
  const perDay = new Map<string, number>();

  for (const row of Object.values(completions)) {
    const at = new Date(row.completed_at);
    if (Number.isNaN(at.getTime())) continue;
    if (at.getHours() < 8) hasEarlyBird = true;
    if (at.getHours() >= 22) hasNightOwl = true;
    if (at.getDay() === 6) sawSaturday = true;
    if (at.getDay() === 0) sawSunday = true;
    const dayKey = `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`;
    perDay.set(dayKey, (perDay.get(dayKey) ?? 0) + 1);
  }

  let maxLessonsInOneDay = 0;
  for (const n of perDay.values()) if (n > maxLessonsInOneDay) maxLessonsInOneDay = n;

  return {
    hasEarlyBird,
    hasNightOwl,
    hasWeekendPair: sawSaturday && sawSunday,
    maxLessonsInOneDay,
  };
}

export type AchievementCategory =
  | 'journey'
  | 'streak'
  | 'perfect'
  | 'level'
  | 'vocab'
  | 'special';

/** Section order + tone identity for the Awards screen. */
export const ACHIEVEMENT_CATEGORIES: AchievementCategory[] = [
  'journey',
  'streak',
  'perfect',
  'level',
  'vocab',
  'special',
];

export interface AchievementDefinition {
  id: string;
  /** Emoji shown in the coin. Replaceable with SVG later. */
  icon: string;
  category: AchievementCategory;
  /** Unlocked when `measure(s) >= target`. */
  target: number;
  measure: (s: ProgressSnapshot) => number;
  /** One-shot badges (yes/no) — hide the numeric x/y counter. */
  binary?: boolean;
}

export const achievementTitleKey = (a: AchievementDefinition) =>
  `achievements.${a.id}.title`;
export const achievementDescriptionKey = (a: AchievementDefinition) =>
  `achievements.${a.id}.description`;

export const isUnlocked = (a: AchievementDefinition, s: ProgressSnapshot) =>
  a.measure(s) >= a.target;
export const progressOf = (a: AchievementDefinition, s: ProgressSnapshot) =>
  Math.min(1, Math.max(0, a.measure(s) / a.target));

const lessonsCompleted = (s: ProgressSnapshot) => Object.keys(s.completions).length;
const perfectLessons = (s: ProgressSnapshot) =>
  Object.values(s.completions).filter((c) => c.perfect).length;
const longestStreak = (s: ProgressSnapshot) => s.streak?.longest_streak ?? 0;

/**
 * v2 award set — 29 entries in 6 categories, each category a tiered ladder
 * so there is always a "next" badge in reach. Adding a badge is one entry
 * here + two i18n strings per locale; no migration needed (unlock state is
 * derived, never stored).
 */
export const ACHIEVEMENTS: AchievementDefinition[] = [
  /* ---------- journey: lessons / skills / units ---------- */
  { id: 'first_step', icon: '👣', category: 'journey', target: 1, measure: lessonsCompleted, binary: true },
  { id: 'lessons_5', icon: '🎯', category: 'journey', target: 5, measure: lessonsCompleted },
  { id: 'lessons_25', icon: '🏅', category: 'journey', target: 25, measure: lessonsCompleted },
  { id: 'lessons_60', icon: '🚀', category: 'journey', target: 60, measure: lessonsCompleted },
  { id: 'lessons_120', icon: '🏆', category: 'journey', target: 120, measure: lessonsCompleted },
  { id: 'skill_first', icon: '🧩', category: 'journey', target: 1, measure: (s) => s.skillsCompleted, binary: true },
  { id: 'skills_10', icon: '🗺️', category: 'journey', target: 10, measure: (s) => s.skillsCompleted },
  { id: 'unit_first', icon: '🏰', category: 'journey', target: 1, measure: (s) => s.unitsCompleted, binary: true },

  /* ---------- streak ---------- */
  { id: 'streak_3', icon: '🔥', category: 'streak', target: 3, measure: longestStreak },
  { id: 'streak_7', icon: '⚡', category: 'streak', target: 7, measure: longestStreak },
  { id: 'streak_14', icon: '🌊', category: 'streak', target: 14, measure: longestStreak },
  { id: 'streak_30', icon: '🌕', category: 'streak', target: 30, measure: longestStreak },
  { id: 'streak_60', icon: '🌋', category: 'streak', target: 60, measure: longestStreak },
  { id: 'streak_100', icon: '💎', category: 'streak', target: 100, measure: longestStreak },

  /* ---------- perfect lessons ---------- */
  { id: 'perfect_1', icon: '⭐', category: 'perfect', target: 1, measure: perfectLessons, binary: true },
  { id: 'perfect_10', icon: '🌟', category: 'perfect', target: 10, measure: perfectLessons },
  { id: 'perfect_25', icon: '✨', category: 'perfect', target: 25, measure: perfectLessons },

  /* ---------- XP levels (level = floor(xp/500)+1) ---------- */
  { id: 'level_2', icon: '🥉', category: 'level', target: 500, measure: (s) => s.totalXp },
  { id: 'level_5', icon: '🥈', category: 'level', target: 2000, measure: (s) => s.totalXp },
  { id: 'level_10', icon: '🥇', category: 'level', target: 4500, measure: (s) => s.totalXp },

  /* ---------- vocabulary ---------- */
  { id: 'vocab_25', icon: '🔤', category: 'vocab', target: 25, measure: (s) => s.vocabCount },
  { id: 'vocab_50', icon: '📖', category: 'vocab', target: 50, measure: (s) => s.vocabCount },
  { id: 'vocab_100', icon: '📗', category: 'vocab', target: 100, measure: (s) => s.vocabCount },
  { id: 'vocab_200', icon: '📚', category: 'vocab', target: 200, measure: (s) => s.vocabCount },

  /* ---------- special ---------- */
  { id: 'early_bird', icon: '🌅', category: 'special', target: 1, measure: (s) => (s.time.hasEarlyBird ? 1 : 0), binary: true },
  { id: 'night_owl', icon: '🦉', category: 'special', target: 1, measure: (s) => (s.time.hasNightOwl ? 1 : 0), binary: true },
  { id: 'weekend', icon: '🏖️', category: 'special', target: 1, measure: (s) => (s.time.hasWeekendPair ? 1 : 0), binary: true },
  { id: 'marathon', icon: '🏃', category: 'special', target: 5, measure: (s) => s.time.maxLessonsInOneDay },
  { id: 'pro_member', icon: '👑', category: 'special', target: 1, measure: (s) => (s.hasProAccess ? 1 : 0), binary: true },
];
